// #region Imports
import { inject, Injectable } from '@angular/core';
import { defer, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { INDEXED_DB_CACHE_VALIDITY_MINUTES, SITECONFIGURATION_CACHE_NAME } from '../app.constants';
import { AppService, Response as ApiResponse } from '../app.service';
import { Country } from '../models/Country';
import { Currency } from '../models/Currency';
import { BootstrapService } from './bootstrap.service';
// #endregion Imports

// #region TypesAndInterfaces
/** Codice di esito positivo nelle risposte del backend. */
const HTTP_STATUS_OK: number = 200;

/** Elenco delle valute salvato in localStorage, con l'utente a cui appartiene e l'istante della lettura. */
interface CurrenciesCacheEntry {
  idUser: number;
  savedAt: number;
  currencies: Currency[];
}

const CURRENCIES_CACHE_STORAGE_KEY: string = 'currenciesCache';
const MILLISECONDS_PER_MINUTE: number = 1000 * 60;
// Stessa validità delle configurazioni di sito. Login, logout e cambio di versione svuotano comunque
// localStorage, e con esso questa copia.
const CURRENCIES_CACHE_VALIDITY_MS: number =
  INDEXED_DB_CACHE_VALIDITY_MINUTES(SITECONFIGURATION_CACHE_NAME) * MILLISECONDS_PER_MINUTE;
// #endregion TypesAndInterfaces

// #region ComponentDecorator
@Injectable({ providedIn: 'root' })
// #endregion ComponentDecorator
export class GeographyService {
  // #region DependencyInjection
  private readonly appService: AppService = inject(AppService);
  private readonly bootstrapService: BootstrapService = inject(BootstrapService);
  // #endregion DependencyInjection

  // #region PublicMethods
  getCountries<T = unknown>(): Observable<ApiResponse<T>> {
    return this.appService.httpGet<T>('Geography', 'GetAllCountries');
  }

  // Esito non positivo (o 401, che arriva come risposta vuota): errore senza motivo, come il
  // vecchio reject() vuoto su cui contano i chiamanti.
  getCountry(idCountry: number): Observable<Country> {
    return this.appService.httpGet<Country>('Geography', `GetCountry/${idCountry}`).pipe(
      switchMap((response: ApiResponse<Country> | undefined): Observable<Country> =>
        response && response.Code == HTTP_STATUS_OK ? of(response.Data) : throwError((): undefined => undefined)
      )
    );
  }

  getStatesByCountry<T = unknown>(idCountry: number): Observable<ApiResponse<T>> {
    return this.appService.httpGet<T>('Geography', `GetStatesByCountry/${idCountry}`);
  }

  /**
   * Valute selezionabili dall'utente di sessione. Si leggono da localStorage finché la copia salvata è dello
   * stesso utente e non è scaduta, altrimenti dal backend; si salva solo una risposta valida.
   * La copia si controlla alla sottoscrizione.
   */
  getCurrencies(): Observable<Currency[]> {
    return defer((): Observable<Currency[]> => {
      const idUser: number | undefined = this.bootstrapService.getUser()?.ID_User;
      const cachedCurrencies: Currency[] | null = this.readCurrenciesFromCache(idUser);

      if (cachedCurrencies !== null) {
        return of(cachedCurrencies);
      }

      return this.appService.httpGet<Currency[]>('Geography', 'GetCurrencies').pipe(
        switchMap((response: ApiResponse<Currency[]> | undefined): Observable<Currency[]> =>
          response && response.Code == HTTP_STATUS_OK ? of(response.Data) : throwError((): undefined => undefined)
        ),
        tap((currencies: Currency[]): void => this.writeCurrenciesToCache(idUser, currencies))
      );
    });
  }
  // #endregion PublicMethods

  // #region PrivateMethods
  /** Copia salvata delle valute: null se manca, non è leggibile, è scaduta o appartiene a un altro utente. */
  private readCurrenciesFromCache(idUser: number | undefined): Currency[] | null {
    if (idUser === undefined || idUser === null) {
      return null;
    }

    try {
      const raw: string | null = this.appService.getLocal(CURRENCIES_CACHE_STORAGE_KEY);
      const entry: CurrenciesCacheEntry | null = raw ? (JSON.parse(raw) as CurrenciesCacheEntry | null) : null;

      if (
        !entry ||
        entry.idUser !== idUser ||
        !Array.isArray(entry.currencies) ||
        typeof entry.savedAt !== 'number' ||
        Date.now() - entry.savedAt >= CURRENCIES_CACHE_VALIDITY_MS
      ) {
        return null;
      }

      return entry.currencies;
    } catch (error) {
      console.error('GeographyService: valute salvate non leggibili', error);
      return null;
    }
  }

  private writeCurrenciesToCache(idUser: number | undefined, currencies: Currency[]): void {
    // Senza utente di sessione, o con una risposta che non è un elenco, non si salva nulla.
    if (idUser === undefined || idUser === null || !Array.isArray(currencies)) {
      return;
    }

    const entry: CurrenciesCacheEntry = { idUser, savedAt: Date.now(), currencies };
    this.appService.setLocal(CURRENCIES_CACHE_STORAGE_KEY, JSON.stringify(entry));
  }
  // #endregion PrivateMethods
}
